import { BLEND_MODES, Container, Graphics, SCALE_MODES, Sprite, Texture } from "pixi.js"
import { Callback } from "game-common/util"
import { COLORS } from "./intro_theme"
import { choreoMs, realMs } from "./intro_pace"
import { ART_PX, PixelBuffer } from "./pixel_raster"
import { LOADING_TITLE_H, TITLE_H, TITLE_STYLE, growBuffers, paintGrowingLetter, titleLetter, trackingFor } from "./pixel_wordmark"
import {
    FLIGHT_DELAY,
    easeOutCubic,
    easeOutExpo,
    flightRuleProgressAt,
    flightSettleTs,
    flightStateAt,
    letterStateAt,
    rampColor,
    ruleProgressAt,
    settleTs,
} from "./logo_choreography"

/** Between letters, in logical pixels. The letters' pixels are ART_PX, like all the pixel art. */
const TRACKING = trackingFor(TITLE_H) * ART_PX

/**
 * Where the letters' centres and the hairline sit in the wordmark's own coordinates: the band the
 * type wordmark's letters occupied, so the tagline and the card keep their places. A letter is
 * TITLE_H + 2 art pixels tall with its outline, so this puts its top on an even logical pixel.
 */
const LETTER_CENTRE_Y = 64
const RULE_Y = 121

/** An offset dark copy behind each letter, in art pixels, so it sits on the night. */
const SHADOW_OFFSET_X = 1
const SHADOW_OFFSET_Y = 2
const SHADOW_ALPHA = 0.45

/** The glow is drawn additively: a translucent orange laid over the night blue mixes to grey. */
const GLOW_GAIN = TITLE_STYLE === "crisp" ? 0.45 : 0.8

/** The tint the letters fly from (the loading screen's own pixels, untinted) and ignite through. */
const PLAIN = 0xffffff

const RULE_WIDTH_RATIO = 0.56
const RULE_CORE_HALF = 1.0
const RULE_GLOW_HALF = 3.0

interface Letter {
    char: string
    container: Container
    glyph: Sprite
    glow: Sprite
    shadow: Sprite
    /** The glyph's advance, in logical pixels. */
    width: number
    /** While it flies: its own buffers, repainted at each size it passes through, and their textures. */
    grow?: { body: PixelBuffer; glow: PixelBuffer; bodyTexture: Texture; glowTexture: Texture; size: number }
}

/** Where one letter of the loading screen's wordmark is, in the wordmark's own coordinates. */
export interface FlightStart {
    /** Centre of the letter. */
    x: number
    y: number
}

export interface FlightOptions {
    from: FlightStart[]
    /** The first frame the letters move: the HTML ones have to go on that same frame. */
    onLiftOff?: () => void
}

interface Flight {
    from: { x: number; y: number }[]
    onLiftOff?: () => void
    liftedOff: boolean
}

const textureOf = (buf: PixelBuffer): Texture =>
    Texture.fromBuffer(new Uint8Array(buf.data.buffer, buf.data.byteOffset, buf.data.byteLength), buf.w, buf.h, {
        scaleMode: SCALE_MODES.NEAREST,
    })

export class ProjectLogo extends Graphics {
    logoText: string = "samsara"

    private letters: Letter[] = []
    private letterWidths: number[] = []
    private restX: number[] = []
    private flight?: Flight
    private totalWidth: number = 0
    /** One body and one glow texture per distinct letter, shared by its repeats. */
    private textures = new Map<string, { body: Texture; glow: Texture }>()

    private rule?: Graphics
    private startedAt: number = 0
    private pulsedAt?: number
    private frameHandle?: number
    private settleHandle?: any
    private disposed: boolean = false

    constructor() {
        super()
        this.build()
    }

    /**
     * Build every letter up front and cache the run's width: `intro.ts` centres the wordmark with it
     * once, so it has to be exact from frame 1.
     */
    private build = () => {
        this.letters = this.logoText.split("").map(this.buildLetter)
        this.letterWidths = this.letters.map(letter => letter.width)
        this.totalWidth =
            this.letterWidths.reduce((sum, w) => sum + w, 0) + TRACKING * Math.max(0, this.letterWidths.length - 1)
    }

    private texturesFor = (char: string) => {
        let textures = this.textures.get(char)
        if (!textures) {
            const art = titleLetter(char)
            textures = { body: textureOf(art.body), glow: textureOf(art.glow) }
            this.textures.set(char, textures)
        }
        return textures
    }

    /**
     * One letter = the pixel art, a dark copy of it for the shadow and its stepped glow, all in art
     * pixels inside a container that maps them to ART_PX logical pixels -- the one size of pixel all
     * the pixel art uses. The container is never scaled beyond that: a letter that changes size is
     * redrawn (see `growLetter`).
     */
    private buildLetter = (char: string): Letter => {
        const textures = this.texturesFor(char)

        const glyph = new Sprite(textures.body)
        glyph.anchor.set(0.5)

        const shadow = new Sprite(textures.body)
        shadow.anchor.set(0.5)
        shadow.tint = COLORS.shadow
        shadow.alpha = SHADOW_ALPHA
        shadow.position.set(SHADOW_OFFSET_X, SHADOW_OFFSET_Y)

        const glow = new Sprite(textures.glow)
        glow.anchor.set(0.5)
        glow.tint = COLORS.accent
        glow.blendMode = BLEND_MODES.ADD
        glow.alpha = 0

        const container = new Container()
        container.addChild(shadow, glow, glyph)
        container.scale.set(ART_PX)

        return { char, container, glyph, glow, shadow, width: titleLetter(char).advance * ART_PX }
    }

    calcWidth = () => this.totalWidth

    /** Where the hairline under the letters sits, in the wordmark's own coordinates. */
    ruleY = () => RULE_Y

    /**
     * A one-off flare over the resting halo. `intro.ts` fires it as the block starts sliding up, so
     * the glow and the motion read as one event rather than two things that happen to overlap.
     */
    pulse = () => {
        this.pulsedAt = Date.now()
    }

    /**
     * Lay the wordmark out and start it. `callback` fires once the letters have landed -- that is
     * the screen's cue to bring in the login card (and, without a flight, to slide the block up).
     *
     * With `flight`, the letters start where the loading screen drew its own small wordmark and fly
     * up into place, so the two screens share one "samsara" instead of crossfading between two.
     */
    update = (callback: Callback<void>, flight?: FlightOptions) => {
        const root = new Container()

        let x = 0
        this.letters.forEach((letter, i) => {
            this.restX[i] = x + this.letterWidths[i] / 2
            letter.container.x = this.restX[i]
            letter.container.y = LETTER_CENTRE_Y
            root.addChild(letter.container)
            x += this.letterWidths[i] + TRACKING
        })

        this.rule = new Graphics()
        root.addChild(this.rule)

        if (flight && flight.from.length === this.letters.length) {
            this.flight = {
                from: flight.from.map(start => ({ x: start.x, y: start.y })),
                onLiftOff: flight.onLiftOff,
                liftedOff: false,
            }
        }

        this.startedAt = Date.now()
        this.applyFrame(0)

        // The letters are driven by animation frames, but the CUE is a timer. A hidden tab gets no
        // frames, and a player who opens the game in a background tab still has to find a login
        // card waiting when they come back -- so the cue never depends on anything having rendered.
        const settleMs = this.flight ? realMs(flightSettleTs(this.letters.length)) : settleTs(this.letters.length)
        this.settleHandle = setTimeout(() => {
            this.liftOff()
            callback()
        }, settleMs)
        this.frameHandle = requestAnimationFrame(this.tick)

        return root
    }

    private tick = () => {
        if (this.disposed) {
            return
        }
        this.applyFrame(Date.now() - this.startedAt)
        this.frameHandle = requestAnimationFrame(this.tick)
    }

    private applyFrame = (elapsed: number) => {
        if (this.flight) {
            this.applyFlight(choreoMs(elapsed))
            return
        }
        const pulseElapsed = this.pulsedAt === undefined ? undefined : Date.now() - this.pulsedAt

        this.letters.forEach((letter, i) => {
            const state = letterStateAt(i, elapsed, pulseElapsed)
            // Rises and fades in at its own size: no grow, pixel art is never scaled.
            letter.container.y = LETTER_CENTRE_Y + state.y
            letter.container.alpha = state.alpha
            letter.glyph.tint = rampColor(COLORS.accent, COLORS.accentHi, PLAIN, state.tintProgress)
            letter.glow.alpha = state.glowAlpha * GLOW_GAIN
        })

        this.drawRule(ruleProgressAt(elapsed, this.letters.length))
    }

    /** One frame of the hand-off flight; `t` is in choreography ms (see intro_pace). */
    private applyFlight = (t: number) => {
        const flight = this.flight!
        if (t >= FLIGHT_DELAY) {
            this.liftOff()
        }
        const pulseElapsed = this.pulsedAt === undefined ? undefined : choreoMs(Date.now() - this.pulsedAt)
        const restY = LETTER_CENTRE_Y

        this.letters.forEach((letter, i) => {
            const state = flightStateAt(i, t, pulseElapsed)
            const from = flight.from[i]
            letter.container.x = from.x + (this.restX[i] - from.x) * state.travel
            letter.container.y = from.y + (restY - from.y) * state.travel
            // It grows on the same eased curve it travels on, redrawn a pixel taller at a time.
            this.growLetter(letter, LOADING_TITLE_H + (TITLE_H - LOADING_TITLE_H) * state.travel)
            letter.container.alpha = 1
            letter.glyph.tint = rampColor(PLAIN, COLORS.accentHi, PLAIN, state.tintProgress)
            letter.glow.alpha = state.glowAlpha * GLOW_GAIN
            letter.shadow.alpha = SHADOW_ALPHA * state.shadow
        })

        this.drawRule(flightRuleProgressAt(t, this.letters.length))
    }

    /**
     * Draw a letter at a fractional x-height: the whole sizes either side cross-faded, into buffers of
     * its own that its sprites show. Held on the glyph's centre, which an odd and an even size can't
     * share exactly.
     */
    private growLetter = (letter: Letter, size: number) => {
        if (!letter.grow) {
            const buffers = growBuffers(letter.char)
            const grow = { ...buffers, bodyTexture: textureOf(buffers.body), glowTexture: textureOf(buffers.glow), size: NaN }
            letter.grow = grow
            letter.glyph.texture = letter.shadow.texture = grow.bodyTexture
            letter.glow.texture = grow.glowTexture
        }
        const grow = letter.grow
        if (grow.size === size) {
            return
        }
        grow.size = size
        const { dx, dy } = paintGrowingLetter(letter.char, size, grow.body, grow.glow)
        grow.bodyTexture.baseTexture.update()
        grow.glowTexture.baseTexture.update()
        letter.glyph.position.set(-dx, -dy)
        letter.glow.position.set(-dx, -dy)
        letter.shadow.position.set(SHADOW_OFFSET_X - dx, SHADOW_OFFSET_Y - dy)
    }

    private liftOff = () => {
        const flight = this.flight
        if (!flight || flight.liftedOff) {
            return
        }
        flight.liftedOff = true
        flight.onLiftOff?.()
    }

    /**
     * The hairline wipes out from the centre.
     *
     * It is one lens-shaped path, not a bar and not a row of segments. A bar has two hard stops
     * and reads as a divider; segments with falling alpha leave a bright seam everywhere two of
     * them overlap. A lens tapers to nothing at both ends in a single fill, which is also the
     * cheaper draw -- and the same "shape as one continuous path" rule the chat bubbles follow.
     */
    private drawRule = (progress: number) => {
        if (!this.rule) {
            return
        }

        this.rule.clear()
        if (progress <= 0) {
            return
        }

        const halfWidth = (easeOutExpo(progress) * this.totalWidth * RULE_WIDTH_RATIO) / 2
        const alpha = easeOutCubic(progress)
        const cx = this.totalWidth / 2
        const y = RULE_Y

        // A quadratic's peak sits halfway to its control point, so the control goes at twice the
        // half-height we actually want.
        const lens = (half: number) => {
            this.rule.moveTo(cx - halfWidth, y)
            this.rule.quadraticCurveTo(cx, y - half * 2, cx + halfWidth, y)
            this.rule.quadraticCurveTo(cx, y + half * 2, cx - halfWidth, y)
            this.rule.closePath()
        }

        // Fatter, dimmer copy for the glow, then the hairline itself. Never a filter -- intro_theme.
        this.rule.beginFill(COLORS.accent, 0.14 * alpha)
        lens(RULE_GLOW_HALF)
        this.rule.endFill()

        this.rule.beginFill(COLORS.accentHi, 0.9 * alpha)
        lens(RULE_CORE_HALF)
        this.rule.endFill()
    }

    /**
     * Stop the loop and free the rasters. A pixi Text's canvas texture is not reclaimed by GC, and
     * the login screen is torn down on every sign-in, so this has to be called -- see
     * `Intro.guestLandingRemover`.
     */
    dispose = () => {
        this.disposed = true
        if (this.frameHandle !== undefined) {
            cancelAnimationFrame(this.frameHandle)
            this.frameHandle = undefined
        }
        if (this.settleHandle !== undefined) {
            clearTimeout(this.settleHandle)
            this.settleHandle = undefined
        }

        // The sprites share each letter's textures, so the sprites go first and the textures once.
        this.letters.forEach(letter => {
            letter.container.destroy({ children: true })
            letter.grow?.bodyTexture.destroy(true)
            letter.grow?.glowTexture.destroy(true)
        })
        this.letters = []
        this.textures.forEach(({ body, glow }) => {
            body.destroy(true)
            glow.destroy(true)
        })
        this.textures.clear()

        this.rule?.destroy()
        this.rule = undefined
    }
}

